import type {ReactElement} from 'react';
import {Text, TextSize} from '@Components/text';
import {selectRemoteSessionsOnPage} from '@Components/poster-editor/poster-presence-reducer';
import {getPageItemTransformVersion} from '@Components/poster-editor/poster-session-reducer';
import type {RemoteSession} from '@PosterWhiteboard/collaboration/poster-presence.types';
import {useAppSelector} from '@/hooks';
import type {PagePresenceLayerProps, RemoteSelectionOutline} from './page-presence-layer.types';
import {getOutlinesBounds, getRemoteSelectionOutlines} from './page-presence-layer.library';
import styles from './page-presence-layer.module.scss';

/** The co-editing design's cursor arrow (Figma "Cursors" component), in its 27x28 box; the tip sits at about (8, 9). */
const CURSOR_ARROW_PATH =
  'M8.36168 9.26537C8.12576 7.50399 10.1299 6.33047 11.5505 7.39815L22.027 15.2717C23.5017 16.3801 22.8211 18.7239 20.9821 18.8699L17.3082 19.1616C16.6981 19.21 16.1437 19.5347 15.8029 20.043L13.7506 23.1042C12.7234 24.6365 10.3463 24.0831 10.1014 22.2547L8.36168 9.26537Z';
/** The arrow's light edge is an inner stroke: drawn at this width and masked to the arrow, half of it shows. */
const CURSOR_ARROW_EDGE_STROKE_WIDTH = 3.4;

/**
 * The other editors' cursors and selections on one page. Rendered inside the page's custom-controls
 * wrapper, which sits over the page in the whiteboard container and mirrors the page's CSS scale, so
 * an outline reaching past the page edge is drawn there rather than clipped by the canvas backdrop,
 * just like this editor's own selection box. Remote positions are canvas pixels; the layer multiplies
 * them by this viewer's zoom.
 */
export function PagePresenceLayer({pageId}: PagePresenceLayerProps): ReactElement | null {
  const sessions = useAppSelector((state) => selectRemoteSessionsOnPage(state, pageId));
  const zoom = useAppSelector((state) => state.posterEditor.posterObject?.scaling.scale ?? 1);
  const itemTransformVersion = useAppSelector((state) => getPageItemTransformVersion(state, pageId));

  if (sessions.length === 0) {
    return null;
  }

  const renderSelection = (session: RemoteSession, outlines: RemoteSelectionOutline[]): ReactElement[] => {
    const boxes = outlines.map((outline) => (
      <div
        key={`${session.sessionId}-${outline.itemId}`}
        className={styles.selection}
        style={{borderColor: session.color, left: outline.left * zoom, top: outline.top * zoom, width: outline.width * zoom, height: outline.height * zoom}}
      />
    ));
    const bounds = getOutlinesBounds(outlines);
    if (bounds === null) {
      return boxes;
    }
    return [
      ...boxes,
      <div key={`${session.sessionId}-name`} className={styles.selectionName} style={{backgroundColor: session.color, left: bounds.left * zoom, top: bounds.top * zoom}}>
        <Text val={session.participant.name} size={TextSize.XS} bold />
      </div>,
    ];
  };

  const renderCursor = (session: RemoteSession): ReactElement | null => {
    if (session.cursor === null) {
      return null;
    }
    const arrowMaskId = `presence-cursor-${session.sessionId}`;
    return (
      <div key={session.sessionId} className={styles.cursor} style={{transform: `translate(${session.cursor.x * zoom}px, ${session.cursor.y * zoom}px)`}}>
        <svg className={styles.cursorArrow} viewBox="0 0 27 28" aria-hidden="true">
          <mask id={arrowMaskId}>
            <path d={CURSOR_ARROW_PATH} fill="white" />
          </mask>
          <path d={CURSOR_ARROW_PATH} fill={session.color} />
          <path d={CURSOR_ARROW_PATH} fill="none" stroke="currentColor" strokeWidth={CURSOR_ARROW_EDGE_STROKE_WIDTH} mask={`url(#${arrowMaskId})`} />
        </svg>
        <div className={styles.cursorName} style={{backgroundColor: session.color}}>
          <Text val={session.participant.name} size={TextSize.XS} bold />
        </div>
      </div>
    );
  };

  /** While a session has an outlined selection, that box and its name pill stand for the session; its cursor would only clutter. */
  const renderSession = (session: RemoteSession): ReactElement[] => {
    const outlines = getRemoteSelectionOutlines(session, itemTransformVersion);
    if (outlines.length > 0) {
      return renderSelection(session, outlines);
    }
    const cursor = renderCursor(session);
    return cursor === null ? [] : [cursor];
  };

  return <div className={styles.layer}>{sessions.flatMap(renderSession)}</div>;
}
